import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Alert, Spinner } from './shared';
import Stepper from './shared/Stepper';
import { TabGeneral, TabPrompt, TabCapabilities, TabToolsSafety, TabReview } from './agent-form';

const STEPS = [
    { id: 'general', label: 'General' },
    { id: 'prompt', label: 'System Prompt' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'tools_safety', label: 'Tools & Safety' },
    { id: 'review', label: 'Review' },
];

const VALID_STEP_IDS = STEPS.map(s => s.id);
const DEFAULT_STEP = 'general';

const NEW_AGENT = {
    id: '', name: '', description: '',
    agent_type: 'default', model_name: 'gemini-3-flash-preview',
    temperature: 1.0, max_tokens: null, max_response_tokens: null,
    reasoning_enabled: true, reasoning_effort: 'medium',
    skills: { enabled: true, include: [], exclude: [], include_tags: [], exclude_tags: [], preload: null, mention: null },
    knowledge: { enabled: true, include: [], exclude: [], include_tags: [], exclude_tags: [], preload: null, mention: null },
    subagents: { enabled: true, include: [], exclude: [], include_tags: [], exclude_tags: [], preload: null, mention: null, stream_subgraphs: true },
    system_prompt: '',
    tools: { enabled: true, include: [], exclude: [], include_tags: [], exclude_tags: [] },
    mcp_enabled: true, mcp_resource_paths: ['system', 'partner', 'user:user_id'],
    mcp_include: null, mcp_exclude: null,
    reminders_enabled: true, reminders_include: null, reminders_exclude: null, reminders_max_per_turn: 5,
    hitl_enabled: true, interrupt_on: null,
    voice_enabled: false, voice_model: null, voice_speed: 1.0,
    owner_type: 'system', owner_id: '', is_public: true, metadata: {},
    environment: null, tags: [],
    context_editing: {},
    summarization: {},
    long_term_memory: {},
    image_search: {},
    visualizer: {},
    sandbox: {},
    tool_search: {},
};

export default function AdminAgentForm({ agentType } = {}) {
    const { agentId } = useParams();
    const navigate = useNavigate();
    const isSubAgent = agentType === 'sub_agent';
    const basePath = isSubAgent ? '/admin/subagents' : '/admin/agents';
    const [searchParams, setSearchParams] = useSearchParams();
    const isCreate = !agentId;

    const tabParam = searchParams.get('tab');
    const activeStep = VALID_STEP_IDS.includes(tabParam) ? tabParam : DEFAULT_STEP;
    const setActiveStep = (next) => {
        const params = new URLSearchParams(searchParams);
        if (next && next !== DEFAULT_STEP) {
            params.set('tab', next);
        } else {
            params.delete('tab');
        }
        setSearchParams(params, { replace: true });
    };

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [promptPreview, setPromptPreview] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);

    const [knowledgeItems, setKnowledgeItems] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [availableSubagents, setAvailableSubagents] = useState([]);
    const [availableTools, setAvailableTools] = useState([]);
    const [toolkits, setToolkits] = useState([]);
    const [availableMCPServers, setAvailableMCPServers] = useState([]);
    const [availableReminders, setAvailableReminders] = useState([]);
    const [interruptDefaults, setInterruptDefaults] = useState({});
    const [mcpServerDetails, setMcpServerDetails] = useState([]);
    const [agentDefaults, setAgentDefaults] = useState({});
    const [environments, setEnvironments] = useState([]);

    useEffect(() => {
        loadData();
    }, [agentId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const initial = isSubAgent ? { ...NEW_AGENT, agent_type: 'sub_agent' } : NEW_AGENT;
            const [agentData] = await Promise.all([
                isCreate ? Promise.resolve(initial) : adminApi.getAgentFull(agentId),
                loadOptions(),
            ]);
            setFormData({ ...agentData });
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    };

    const loadOptions = async () => {
        setLoadingOptions(true);
        try {
            const [skillRes, subRes, toolRes, tkRes, mcpRes, intRes, remRes, defRes, knRes, envRes] = await Promise.all([
                adminApi.listSkills().catch(() => ({ skills: [] })),
                adminApi.listAgents({ agent_type: 'sub_agent' }).catch(() => ({ agents: [] })),
                adminApi.listTools().catch(() => ({ tools: [] })),
                adminApi.listToolkits().catch(() => ({ toolkits: [] })),
                adminApi.listMCPServers().catch(() => ({ servers: [] })),
                adminApi.getInterruptDefaults().catch(() => ({ tools: {} })),
                adminApi.listReminders().catch(() => ({ reminders: [] })),
                adminApi.getAgentDefaults().catch(() => ({ defaults: {} })),
                adminApi.listKnowledge().catch(() => ({ knowledge_files: [] })),
                isSubAgent ? adminApi.listEnvironments().catch(() => ({ environments: [] })) : Promise.resolve({ environments: [] }),
            ]);
            setEnvironments(envRes.environments || []);
            setKnowledgeItems((knRes.knowledge_files || []).map(k => ({
                id: k.id,
                name: k.name,
                topic: k.topic,
                description: k.description,
                tags: k.tags || [],
            })));
            setAvailableSkills((skillRes.skills || []).map(s => ({ value: s.id, label: s.name || s.id, tags: s.tags || [] })));
            setAvailableSubagents((subRes.agents || []).map(s => ({ value: s.id, label: s.name || s.id, tags: s.tags || [] })));
            setAvailableTools((toolRes.tools || []).map(t => ({ value: t.name, label: t.name, is_mcp: t.is_mcp || false, tags: t.tags || [] })));
            setToolkits((tkRes.toolkits || []).map(tk => ({
                value: tk.name,
                label: tk.name,
                tool_count: tk.tool_count || 0,
                tools: tk.tools || [],
            })));
            setAvailableMCPServers((mcpRes.servers || []).map(s => ({ value: s.id, label: s.name || s.id })));
            setAvailableReminders((remRes.reminders || []).map(r => ({ value: r.id, label: `${r.name} (${r.trigger})` })));
            setInterruptDefaults(intRes.tools || {});
            setMcpServerDetails((mcpRes.servers || []).map(s => ({
                id: s.id, name: s.name || s.id, tools: s.tools || [],
            })));
            setAgentDefaults(defRes.defaults || {});
        } catch {} finally {
            setLoadingOptions(false);
        }
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = { ...formData };
            if (isSubAgent) payload.agent_type = 'sub_agent';
            if (isCreate) {
                await adminApi.createAgent(payload);
            } else {
                await adminApi.updateAgent(payload.id, payload);
                await adminApi.clearAgentCache(payload.id);
            }
            navigate(basePath);
        } catch (err) {
            setError(err.message);
            setActiveStep('general');
        } finally {
            setSaving(false);
        }
    };

    const currentIndex = STEPS.findIndex(s => s.id === activeStep);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === STEPS.length - 1;

    const goNext = () => {
        if (isLast) {
            handleSubmit();
        } else {
            setActiveStep(STEPS[currentIndex + 1].id);
        }
    };

    const goBack = () => {
        if (!isFirst) setActiveStep(STEPS[currentIndex - 1].id);
    };

    if (loading) return <Spinner size="lg" text="Loading..." center />;
    if (!formData) return <Alert variant="error">Agent not found</Alert>;

    return (
        <div>
            <div className="text-center mb-2">
                <h1 className="text-xl font-bold text-gray-900">
                    {isCreate ? 'Create Agent' : `Edit: ${formData.name}`}
                </h1>
                {!isCreate && <p className="text-sm text-gray-500 mt-1">{agentId}</p>}
                {isCreate && <p className="text-sm text-gray-500 mt-1">Configure your agent step by step</p>}
            </div>

            <Stepper steps={STEPS} activeIndex={currentIndex} onChange={setActiveStep} />

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <form onSubmit={handleSubmit}>
                {activeStep === 'general' && (
                    <TabGeneral
                        formData={formData}
                        handleChange={handleChange}
                        isCreate={isCreate}
                        isSubAgent={isSubAgent}
                        environments={environments}
                    />
                )}

                {activeStep === 'prompt' && (
                    <TabPrompt
                        formData={formData}
                        handleChange={handleChange}
                        promptPreview={promptPreview}
                        setPromptPreview={setPromptPreview}
                    />
                )}

                {activeStep === 'capabilities' && (
                    <TabCapabilities
                        formData={formData}
                        handleChange={handleChange}
                        isCreate={isCreate}
                        knowledgeItems={knowledgeItems}
                        availableSkills={availableSkills}
                        availableSubagents={availableSubagents}
                        availableReminders={availableReminders}
                        availableTools={availableTools}
                        agentDefaults={agentDefaults}
                        loadingOptions={loadingOptions}
                    />
                )}

                {activeStep === 'tools_safety' && (
                    <TabToolsSafety
                        formData={formData}
                        handleChange={handleChange}
                        availableTools={availableTools}
                        toolkits={toolkits}
                        availableMCPServers={availableMCPServers}
                        interruptDefaults={interruptDefaults}
                        mcpServerDetails={mcpServerDetails}
                        agentDefaults={agentDefaults}
                        loadingOptions={loadingOptions}
                    />
                )}

                {activeStep === 'review' && (
                    <TabReview
                        formData={formData}
                        isCreate={isCreate}
                        knowledgeItems={knowledgeItems}
                        onEditSection={setActiveStep}
                    />
                )}

                <div className="flex items-center justify-center gap-3 pt-6 pb-4">
                    {!isFirst && (
                        <Button variant="ghost" type="button" onClick={goBack}>
                            Back
                        </Button>
                    )}
                    {!isLast && (
                        <Button type="button" onClick={goNext}>
                            Next
                        </Button>
                    )}
                    {!isCreate && !isLast && (
                        <Button variant="secondary" type="button" onClick={handleSubmit} loading={saving}>
                            Save Changes
                        </Button>
                    )}
                    {isLast && (
                        <Button type="button" onClick={goNext} loading={saving}>
                            {isCreate ? 'Create Agent' : 'Save Changes'}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
}
